/* @/plugins/api.js
 * author : rackyz
 * date   : 2021/10/21
 * update : 2022/05/27
 * desc   : Enclose 'axios' with HSAPI, to create default api object and binding them to VUE.prototype. You can use 'this.api' to call each service.
 *   2022-05-27:
 *     1. removed API name style interfaces, replaced with axios original methods to adapt restful API more natrually
 *     2. [store] In vuex/store only need to
 *        import {API,COS} from '@/plugins/api'
 *     3. [vue] Installed $api,$cos to Vue prototype
 *     4. api methods:
 *        - SetAuthentication: set headers.Authentication to identify user cerification
 *        - SetEnterprise: set headers.Enterprise to identify Enterprise certification
 *        - Clear: clear all user login session state in api state and localStorage
 *        -
 *
 *
 */

import axios from 'axios';
import config from '../config';
import { streamAiChatXhr } from '../components/ai/ai-chat-stream';
const CancelToken = axios.CancelToken;
const source = CancelToken.source;
import bus from './bus';
const o = {
  config,
};

// Axios Server to backends Server
export const API = axios.create({
  baseURL: config.server,
  timeout: 600000,
});

export const AI = axios.create({
  baseURL: config.ai_server,
  timeout: 600000,
});

export const COS = axios.create({
  baseURL: config.cosServer,
});

// default api-version
API.defaults.headers = {
  'api-version': config.apiVersion || 'v0',
};


const requestThreads = {};

API.interceptors.request.use(async (config) => {
  // if (requestThreads[config.url+(config.query?JSON.parse(config.query):"")] == undefined) {
  //   config.cancelToken = new CancelToken(function (c) {
  //     requestThreads[config.url+(config.query?JSON.parse(config.query):"")] = c
  //   })
  //   return config
  // } else {
  //   if (config.url.includes('files') == false &&  typeof requestThreads[config.url+(config.query?JSON.parse(config.query):"")] == 'function') {
  //     requestThreads[config.url+(config.query?JSON.parse(config.query):"")](config.url)
  //     requestThreads[config.url+(config.query?JSON.parse(config.query):"")] = setTimeout(() => {
  //       delete requestThreads[config.url+(config.query?JSON.parse(config.query):"")]
  //     }, 500)
  //   }

  //   return config
  // }
  return config;
}, (error) => Promise.reject(error));

// axios response hooks
API.interceptors.response.use((data) => {
  if (data.config && data.config.url) {
    clearTimeout(requestThreads[data.config.url]);
    delete requestThreads[data.config.url];
  }
  if (data && data.data && data.data.code === -1) {
    const msg = data.data.error ? data.data.error : (data.data.message ? data.data.message : '未知');
    const err = new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    err.response = data;
    err.apiBody = data.data;
    err.apiData = data.data.data;
    return Promise.reject(err);
  }

  return data;
}, (err) => {
  if (axios.isCancel(err)) {
    return Promise.reject('抖动已清除:'+err);
  }
  if (err.message && err.message.includes('timeout')) {
    return Promise.reject('网络连接超时');
  }

  if (err.response) {
    const message = err.response?.data?.message || '无';
    const wrap = (text) => {
      const e = new Error(text);
      e.response = err.response;
      e.apiBody = err.response && err.response.data;
      e.apiData = err.response && err.response.data && err.response.data.data;
      return Promise.reject(e);
    };
    if (err.response.status == 504 || err.response.status == 404) {
      return wrap(`404-请求资源不存在:${message}`);
    } else if (err.response.status == 500) {
      return wrap(`500-服务器内部错误:${message}`);
    } else if (err.response.status == 403) {
      API.ClearAll();
      bus.$emit('401');
      return wrap(`403-权限不足:${message}`);
     
    } else if (err.response.status == 401) {
      API.ClearAll();
      bus.$emit('401');
      return Promise.reject('401-登录状态已过期');
    } else {
      return wrap(message !== '无' ? message : (err.message || '请求失败'));
    }
  }

  if (err.config && err.config.method == 'options') {
    return Promise.reject('服务器访问失败');
  }
  return Promise.reject(err);
});

// set header.Authorization
AI.SetAuthorization = function(token) {
  if (token) {
    AI.defaults.headers.Authorization = token;
  }
};

/**
 * 流式对话（与 inbgz-assistant 入参一致），返回 { reader, contentType }。
 * ZZL 工作台等应使用 `AI.chat`，勿依赖 `this.$ai`（可能被 AiBot 替换为 SDK 实例）。
 */
AI.chat = async function chat(payload) {
  const base = (config.ai_server || '').replace(/\/$/, '');
  const url = `${base}/chat`;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream, application/json, text/plain, */*',
  };
  if (AI.defaults.headers && AI.defaults.headers.Authorization) {
    headers.Authorization = AI.defaults.headers.Authorization;
  }
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const t = await res.text();
      if (t) {
        try {
          const j = JSON.parse(t);
          msg = j.message || j.error || j.msg || t;
        } catch (e) {
          msg = t;
        }
      }
    } catch (e) { /* ignore */ }
    throw new Error(msg || `请求失败 ${res.status}`);
  }
  if (!res.body || typeof res.body.getReader !== 'function') {
    throw new Error('当前环境不支持流式响应');
  }
  return {
    reader: res.body.getReader(),
    contentType: (res.headers.get('Content-Type') || '').toLowerCase(),
  };
};

/**
 * 流式对话（XHR onprogress，页面可实时增量渲染）
 * @param {object} payload
 * @param {(fullText: string) => void} onUpdate
 * @returns {Promise<string>}
 */
AI.streamChat = function streamChat(payload, onUpdate) {
  const base = (config.ai_server || '').replace(/\/$/, '');
  const url = `${base}/chat`;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream, application/json, text/plain, */*',
  };
  if (AI.defaults.headers && AI.defaults.headers.Authorization) {
    headers.Authorization = AI.defaults.headers.Authorization;
  }
  return streamAiChatXhr({ url, headers, body: payload }, onUpdate);
};

// set header.Authorization
API.SetAuthorization = function(token) {
  if (token) {
    API.defaults.headers.Authorization = token;
  }
};

// set header.Enterprise
API.SetEnterprise = function(ent_id) {
  if (ent_id) {
    API.defaults.headers["x-enterprise"] = ent_id;
  }
};

API.afterLogin = (fn, handler, delayTime=100)=>{
  if (handler) {
    clearTimeout(handler);
  }
  return setTimeout(()=>{
    if (API.defaults.headers.Authorization) {
      fn();
    } else {
      bus.$once('login', fn);
    }
  }, delayTime);
};

// remove header.Enterprise
API.ClearEnterprise = function() {
  delete API.defaults.headers.Enterprise;
};

API.getCounts = async (url, {filter, count_by})=>{
  let computedUrl = url+'?q=count';
  if (count_by) {
    computedUrl += `&count_by=${count_by}`;
  }
  if (filter && typeof filter == 'object') {
    for (const key in filter) {
      if (key && filter[key] !== undefined) {
        computedUrl += `&${key}=${filter[key]}`;
      }
    }
  }
  return API.get(computedUrl);
};

API.getAll = async (url, {filter, order, related})=>{
  let computedUrl = url+'?';
  if (filter && typeof filter == 'object') {
    for (const key in filter) {
      if (key && filter[key] !== undefined) {
        let value = filter[key];
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value = value.join(',');
          } else {
            continue;
          }
        }
        computedUrl += `&${key}=${value}`;
      }
    }
  }
  if (order && typeof order == 'object' && order.key) {
    computedUrl += `&order=${order.key}&desc=${order.order == 'desc'?1:0}`;
  }

  if (Array.isArray(related)) {
    computedUrl += `&related=${related.join(',')}`;
  }

  return API.get(computedUrl);
};

API.getPaged = async (url, {page, pagesize,plain, filter, order, related})=>{
  let computedUrl = url+'?q=page';
  computedUrl += `&page=${page}`;
  if (plain) {
    computedUrl += `&plain=1`;
  }
  if (pagesize) {
    computedUrl += `&pagesize=${[pagesize]}`;
  }
  if (filter && typeof filter == 'object') {
    for (const key in filter) {
      if (key && filter[key] !== undefined) {
        let value = filter[key];
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (Array.isArray(value[0])) {
              value = value.map((v)=>v.join(',')).join(';');
            } else {
              value = value.join(',');
            }
          } else {
            continue;
          }
        }
        computedUrl += `&${key}=${value}`;
      }
    }
  }
  if (order && typeof order == 'object' && order.key) {
    computedUrl += `&order=${order.key}&desc=${order.order == 'desc'?1:0}`;
  }

  if (Array.isArray(related)) {
    computedUrl += `&related=${related.join(',')}`;
  }

  return API.get(computedUrl);
};

// clear all login session state
API.ClearAll = function() {
  delete API.defaults.headers.Authorization;
  delete API.defaults.headers.Enterprise;
  localStorage.removeItem('hs-token');
  localStorage.removeItem('current_enterprise');
};

o.install = async (vue) => {
  let token = localStorage.getItem('hs-token');
  if (!token) {
    try {
      const raw = localStorage.getItem('session');
      if (raw) {
        const s = JSON.parse(raw);
        if (s && s.token) token = s.token;
      }
    } catch (e) {
      /* ignore */
    }
  }
  if (token) {
    API.SetAuthorization(token);
    AI.SetAuthorization(token);
  }
  vue.prototype.$api = API;
  vue.prototype.$cos = COS;
  vue.prototype.$ai = AI;
};

o.API = API;
o.COS = COS;
o.AI = AI;


function GetObjectURL(file) {
  if (file && file.uri) {
    return file.uri;
  }
  let url = null;
  if (window.createObjectURL != undefined) {
    // basic
    url = window.createObjectURL(file);
  } else if (window.URL != undefined) {
    // mozilla(firefox)
    url = window.URL.createObjectURL(file);
  } else if (window.webkitURL != undefined) {
    // webkit or chrome
    url = window.webkitURL.createObjectURL(file);
  }
  return url;
}

function GetFileName(url) {
  if (url) {
    const fileName = url.replace(/(.*\/)*([^.]+)/i, '$2');
    return fileName;
  }
}

function GetFileExt(url) {
  if (url) {
    const ext = url.substring(url.lastIndexOf('.') + 1);
    return ext;
  }
}


/** upload
 *  params:
 *    files - e.srcElement.files
 *    callback:{
 *       onPreload
 *       onProgress
 *       onSuccess
 *       onFail
 *    }
 *   config:{
 *
 *   }
 */

function isImage(ext) {
  return ['webp', 'gif', 'jpg', 'jpeg', 'png'].includes(ext);
}

COS.upload = async (files, {
  onPreload,
  onProgress,
  onFail,
  onSuccessFile,
  onSuccess,
}, {
    coskey,
    vdisk,
  }={}) => {
  if (!files) return;
  console.log('cos:',files.length,files)
  const fileObjects = files.map((f) => {
    return {
      name: f.name,
      size: f.size,
      ext: GetFileExt(f.name),
      url: GetObjectURL(f),
      percent: 0,
      loading: true,
      vdisk: vdisk || 'ref',

      cancel() {
        if (this.source) {
          this.source.cancel('取消');
        }
      },
    };
  });


  // 1 - 预加载：将图片文件转换为ObjectURL
  //     此处可以获取当前文件对象的信息
  if (onPreload) {
    console.log('on-preload',fileObjects.length)
    onPreload(fileObjects);
  }

  return new Promise((resolve, reject)=>{
    // 2 - 上传文件信息到文件服务器，换取文件ID
    API.post('files', fileObjects.map((v)=>({
      name: v.name,
      size: v.size,
      ext: v.ext,
      url: v.url,
      vdisk: v.vdisk,
      loading: v.loading,
    }))).then((res) => {
    // 返回文件ID
      const resFiles = res.data.data;
      console.log('pre-upload-files success');
      // 3 - 将文件以新ID命名，上传文件到cos
      return Promise.all(
          fileObjects.map((v, i, a) => {
            a[i].loading = true;
            a[i].id = resFiles[i].id;
            const file = files[i];
            const filekey = 'files/' + resFiles[i].url;
            file.loading = true;
            const formdata = new FormData();
            formdata.append('key', filekey);
            formdata.append('file', files[i]);
            fileObjects[i].source = CancelToken.source();

            // 对较大的图片 上传略缩图
            if (isImage(v.ext) && v.size > 1024*3) {
              console.log('upload mini');
              createImageBitmap(files[i]).then((image)=>{
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = 32;
                canvas.height = 32;
                // 核心JS就这个
                context.drawImage(image, 0, 0, 32, 32);
                canvas.toBlob((blob)=>{
                  const file = new File([blob], 'image_32x32.png', {type: blob.type});
                  const s_formdata = new FormData();
                  const s_filekey = 'files/' + resFiles[i].url.replace(`.${v.ext}`, `-32x32.${v.ext}`);
                  console.log(s_filekey);
                  s_formdata.append('key', s_filekey);
                  s_formdata.append('file', file);
                  COS.post('', s_formdata, {
                    'content-type': 'multipart/form-data',
                    'headers': {
                      Authorization: coskey,
                    }}).then(()=>{
                    console.log('32x32-success', s_filekey);
                  }).catch((e)=>{
                    console.error('32-error', e);
                  });
                });
                canvas.remove();
              });
            }


            return new Promise((resolve, reject)=>{
              return COS.post('', formdata, {
                'content-type': 'multipart/form-data',
                'headers': {
                  Authorization: coskey,
                },
                'cancelToken': fileObjects[i].source.token,
                'onUploadProgress': (progressEvent) => {
                  if (!progressEvent) {
                    return;
                  }
                  const complete = (progressEvent.loaded / progressEvent.total * 100 | 0) + '%';
                  fileObjects[i].percent = complete;
                  if (onProgress) {
                    onProgress(fileObjects[i]);
                  }
                  console.log('progress');
                },
              }).then(() => {
                // 3.Success - 单项文件上传完毕
                //    替换链接地址为cos地址
                console.log('cos-success');
                //    移除控制器和加载状态
                return API.post('files/success', fileObjects.map((v)=>v.id)).then(() => {
                  fileObjects[i].url = COS.defaults.baseURL + filekey;
                  delete a[i].loading;
                  delete a[i].percent;
                  delete fileObjects[i].source;
                  if (onSuccessFile) {
                    onSuccessFile(a[i], i);
                  }
                  resolve(a[i]);
                });
              }).catch((e) => {
                console.log('cos-error', e);

                if (e && e.message == '取消上传') {
                  a[i].canceled = true;
                  return;
                }
                API.post('files/cancel', fileObjects.map((v) =>v.id)).then(()=>{
                  reject(e);
                }).catch((e)=>{
                  reject(e);
                });
              });
            });
          }),
      ).then(() => {
        console.log('promise-all success');
        if (onSuccess) {
          onSuccess(fileObjects);
        }
        resolve(fileObjects);
      }).catch((e)=>{
        console.error('promise.all-error', e);
        if (onFail) {
          onFail(e);
        }
      });
    }).catch((e)=>{
      if (onFail) {
        onFail(e);
      }
      console.error('文件信息上传失败', e);
    });
  });
};

export default o;
